import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown } from 'lucide-react'
import { cn } from 'utils/cn'
import { POPOVER_Z_INDEX } from './z-index'

/** How long a type-ahead buffer stays alive between keystrokes, matching native <select> behaviour. */
const TYPE_AHEAD_RESET_MS = 600

export interface SelectOption {
    label: string
    value: string
    disabled?: boolean
    /** Divergence from @numeo-ai/components, matching ds2 `Dropdown`/`ListBox` items: a leading
     *  icon (also shown on the trigger when selected) and a muted second line under the label. */
    prefix?: React.ReactNode
    description?: string
}

export interface SelectSection {
    title?: string
    items: SelectOption[]
}

export interface SelectProps {
    variant?: 'primary' | 'secondary'
    label?: string
    required?: boolean
    placeholder?: string
    sections?: SelectSection[]
    options?: SelectOption[]
    value?: string
    defaultValue?: string
    onChange?: (value: string) => void
    disabled?: boolean
    error?: string
    style?: React.CSSProperties
    /** Test hook rendered on the trigger button. Divergence from @numeo-ai/components:
     *  without it, testids passed to Select silently vanish (no prop spread). */
    'data-testid'?: string
    /** Accessible name for the trigger when there is no visible `label` (e.g. a "Rows per page" select). */
    'aria-label'?: string
}

const SelectItem = ({
    item,
    isSelected,
    isFocused,
    onSelect,
    onHover,
}: {
    item: SelectOption
    isSelected: boolean
    isFocused: boolean
    onSelect: (val: string) => void
    onHover: () => void
}) => {
    const itemRef = useRef<HTMLButtonElement>(null)

    // Keep the keyboard-focused option visible: the list caps at 320px with internal scrolling,
    // so ArrowUp/Down could otherwise walk the focus below the fold invisibly. Scroll the list's
    // own scrollTop instead of scrollIntoView — the latter may also scroll the document, which
    // the close-on-outer-scroll listener would treat as "close the list".
    useEffect(() => {
        if (!isFocused || !itemRef.current) return
        const item = itemRef.current
        const list = item.closest<HTMLElement>('[role="listbox"]')
        if (!list) return
        const itemTop = item.offsetTop
        const itemBottom = itemTop + item.offsetHeight
        if (itemTop < list.scrollTop) list.scrollTop = itemTop
        else if (itemBottom > list.scrollTop + list.clientHeight) list.scrollTop = itemBottom - list.clientHeight
    }, [isFocused])

    return (
        <button
            ref={itemRef}
            type="button"
            role="option"
            aria-selected={isSelected}
            disabled={item.disabled}
            onClick={() => !item.disabled && onSelect(item.value)}
            onMouseEnter={onHover}
            className={cn(
                'flex min-h-9 w-full items-center gap-3 rounded-[20px] border-none px-3 py-1.5 text-left text-sm font-medium text-default-foreground outline-none transition-[background] duration-100',
                isSelected || isFocused ? 'bg-default' : 'bg-transparent',
                item.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
            )}
        >
            {item.prefix && <span className="flex shrink-0 items-center">{item.prefix}</span>}
            {item.description ? (
                <span className="flex min-w-0 flex-1 flex-col">
                    <span>{item.label}</span>
                    <span className="text-xs font-normal leading-4 text-foreground-muted">{item.description}</span>
                </span>
            ) : (
                item.label
            )}
        </button>
    )
}

export const Select = ({
    variant = 'primary',
    label,
    required = false,
    placeholder = 'Select one',
    sections,
    options,
    value: controlledValue,
    defaultValue,
    onChange,
    disabled = false,
    error,
    style,
    'data-testid': dataTestId,
    'aria-label': ariaLabel,
}: SelectProps) => {
    const isSecondary = variant === 'secondary'
    const [open, setOpen] = useState(false)
    const [internalValue, setInternalValue] = useState<string | undefined>(defaultValue)
    const [focusedIndex, setFocusedIndex] = useState(-1)
    const typeAheadRef = useRef({ query: '', at: 0 })
    const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null)
    const [listPos, setListPos] = useState<{ top: number; left: number; width: number } | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const listRef = useRef<HTMLDivElement>(null)

    const value = controlledValue !== undefined ? controlledValue : internalValue

    const allItems = sections ? sections.flatMap((s) => s.items) : (options ?? [])
    const enabledItems = allItems.filter((o) => !o.disabled)
    const resolvedSections = sections ?? (options ? [{ items: options }] : [])
    const selectedOption = allItems.find((o) => o.value === value)

    const openList = () => {
        const rect = triggerRef.current?.getBoundingClientRect()
        if (rect) setAnchorRect(rect)
        setListPos(null)
        setOpen(true)
        // Start keyboard focus on the currently selected option (not the first one) so an
        // Enter→Enter round-trip re-confirms the current value instead of silently switching
        // to the first option in the list.
        const selectedIndex = enabledItems.findIndex((o) => o.value === value)
        setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0)
    }

    useEffect(() => {
        // Portaled to <body>, so the list is no longer a DOM descendant of containerRef —
        // check both refs or every click registers as "outside" and closes it immediately.
        const handler = (e: MouseEvent) => {
            const target = e.target as Node
            if (containerRef.current?.contains(target)) return
            if (listRef.current?.contains(target)) return
            setOpen(false)
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    useEffect(() => {
        // The panel is portaled to <body> with a position computed once at open time, so it can't
        // track the trigger as the page (or any scrollable ancestor) scrolls — closing on scroll
        // avoids the panel visually drifting away from its trigger. Scroll events bubbling from
        // inside the list itself (its own overflow-y:auto content) must be ignored here, otherwise
        // scrolling through a long option list would immediately close the list you're scrolling.
        if (!open) return
        const handleScroll = (e: Event) => {
            if (listRef.current?.contains(e.target as Node)) return
            setOpen(false)
        }
        window.addEventListener('scroll', handleScroll, true)
        return () => window.removeEventListener('scroll', handleScroll, true)
    }, [open])

    // Runs before paint: measures the list's real (content-driven) height against the anchor's
    // position and flips it above the trigger when there isn't room below, plus caps its height so
    // long option lists (e.g. all 50 US states) scroll internally instead of rendering past the
    // bottom of the viewport with no way to reach the cut-off items.
    useLayoutEffect(() => {
        if (!open || !anchorRect || !listRef.current) return
        const listHeight = listRef.current.offsetHeight
        const spaceBelow = window.innerHeight - anchorRect.bottom
        const spaceAbove = anchorRect.top
        const top =
            spaceBelow >= listHeight + 8 || spaceBelow >= spaceAbove
                ? Math.min(anchorRect.bottom + 8, window.innerHeight - listHeight - 8)
                : Math.max(8, anchorRect.top - listHeight - 8)
        setListPos({ top, left: anchorRect.left, width: anchorRect.width })
    }, [open, anchorRect])

    const handleSelect = useCallback(
        (val: string) => {
            if (controlledValue === undefined) setInternalValue(val)
            onChange?.(val)
            setOpen(false)
        },
        [controlledValue, onChange]
    )

    // Type-ahead: jump to the first option starting with the typed characters, matching what a native
    // <select> does. Migrating a native select to this component would otherwise silently drop it, and
    // it is the only practical way to reach a far-down option (a dispatcher, a state) from the keyboard.
    const handleTypeAhead = (key: string) => {
        if (enabledItems.length === 0) return

        const now = performance.now()
        const query = (now - typeAheadRef.current.at > TYPE_AHEAD_RESET_MS ? '' : typeAheadRef.current.query) + key.toLowerCase()
        typeAheadRef.current = { query, at: now }

        // Native <select> treats one character pressed repeatedly ("s", "s", "s") as "walk through the
        // options starting with s", not as the prefix "sss" — which would match nothing and leave the
        // list stuck on the first s-option. Collapse an all-same-character buffer back to that single
        // character and start the scan after the current option, wrapping, so each press advances.
        const isRepeatedKey = [...query].every((char) => char === query[0])
        const prefix = isRepeatedKey ? query[0] : query
        const currentIndex = open ? focusedIndex : enabledItems.findIndex((o) => o.value === value)
        // A multi-character prefix must be able to match the option it is already on ("j" → John,
        // then "o" → "jo" still means John), so only a cycling press skips the current option.
        const start = isRepeatedKey ? currentIndex + 1 : Math.max(currentIndex, 0)

        let matchIndex = -1
        for (let offset = 0; offset < enabledItems.length; offset++) {
            const index = (start + offset + enabledItems.length) % enabledItems.length
            if (enabledItems[index].label.toLowerCase().startsWith(prefix)) {
                matchIndex = index
                break
            }
        }
        if (matchIndex === -1) return

        if (open) {
            setFocusedIndex(matchIndex)
        } else {
            handleSelect(enabledItems[matchIndex].value)
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (disabled) return
        // Single printable characters only — modifier combos stay available as browser/OS shortcuts.
        if (e.key.length === 1 && e.key !== ' ' && !e.ctrlKey && !e.metaKey && !e.altKey) {
            e.preventDefault()
            handleTypeAhead(e.key)
            return
        }
        if (!open) {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                e.preventDefault()
                openList()
            }
            return
        }
        if (e.key === 'Escape') {
            e.preventDefault()
            // Consume the Escape entirely: ds2 Modal listens for Escape on `document` and closes
            // itself, so without stopping propagation one keypress would close both the option
            // list AND the parent modal, discarding the user's in-progress form.
            e.stopPropagation()
            setOpen(false)
            setFocusedIndex(-1)
        } else if (e.key === 'ArrowDown') {
            e.preventDefault()
            setFocusedIndex((i) => Math.min(i + 1, enabledItems.length - 1))
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setFocusedIndex((i) => Math.max(i - 1, 0))
        } else if (e.key === 'Enter' && focusedIndex >= 0) {
            e.preventDefault()
            handleSelect(enabledItems[focusedIndex].value)
        }
    }

    return (
        <div ref={containerRef} className="relative flex w-full flex-col gap-1.5" style={style}>
            {label && (
                <label className="inline-flex flex-wrap items-center gap-1 whitespace-nowrap pr-2 text-sm font-medium text-foreground">
                    <span>{label}</span>
                    {required && <span className="text-danger">*</span>}
                </label>
            )}

            <button
                ref={triggerRef}
                type="button"
                data-testid={dataTestId}
                aria-label={ariaLabel}
                disabled={disabled}
                onClick={() => {
                    if (!disabled) {
                        if (open) {
                            setOpen(false)
                        } else {
                            openList()
                        }
                    }
                }}
                onKeyDown={handleKeyDown}
                data-field-box
                className={cn(
                    'relative flex h-9 w-full items-center overflow-hidden rounded-xl p-0 text-left outline-none transition-[box-shadow] duration-150',
                    isSecondary ? 'bg-default' : 'bg-surface',
                    error
                        ? 'border-[1.5px] border-solid border-danger ring-2 ring-danger'
                        : cn('border-none focus-visible:ring-2 focus-visible:ring-accent', !isSecondary && 'shadow-field'),
                    disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                )}
            >
                <div className="flex flex-1 items-center gap-2 overflow-hidden pl-3">
                    {selectedOption?.prefix && <span className="flex shrink-0 items-center">{selectedOption.prefix}</span>}
                    <span className={cn('whitespace-nowrap text-sm leading-5', selectedOption ? 'text-foreground' : 'text-foreground-muted')}>
                        {selectedOption ? selectedOption.label : placeholder}
                    </span>
                </div>
                <span className={cn('flex h-9 shrink-0 items-center justify-center px-3 py-2', error ? 'text-danger' : 'text-foreground')}>
                    <ChevronDown size={16} strokeWidth={1.5} className={cn('transition-transform duration-200', open ? 'rotate-180' : 'rotate-0')} />
                </span>
            </button>

            {error && <span className="text-xs text-danger">{error}</span>}

            {open &&
                anchorRect &&
                createPortal(
                    <div
                        ref={listRef}
                        role="listbox"
                        // Hover sets `focusedIndex` and nothing ever cleared it, so the last option
                        // the pointer crossed stayed highlighted after the mouse left the list —
                        // reading as "this one is selected" when nothing had been chosen.
                        onMouseLeave={() => setFocusedIndex(-1)}
                        style={{
                            position: 'fixed',
                            top: listPos ? listPos.top : anchorRect.bottom + 8,
                            left: listPos ? listPos.left : anchorRect.left,
                            width: listPos ? listPos.width : anchorRect.width,
                            maxHeight: Math.min(320, window.innerHeight - 16),
                            overflowY: 'auto',
                            zIndex: POPOVER_Z_INDEX,
                            visibility: listPos ? 'visible' : 'hidden',
                        }}
                        className="flex flex-col gap-0.5 rounded-3xl bg-surface p-1 shadow-overlay backdrop-blur-[20px]"
                    >
                        {resolvedSections.map((section, si) => (
                            <React.Fragment key={si}>
                                {si > 0 && (
                                    <div className="my-0.5 px-3">
                                        <div className="h-px bg-border" />
                                    </div>
                                )}
                                {section.title && <div className="px-3 pb-1 pt-2.5 text-xs font-medium text-foreground-muted">{section.title}</div>}
                                {section.items.map((item) => {
                                    const isSelected = value === item.value
                                    const enabledIndex = enabledItems.indexOf(item)
                                    const isFocused = enabledIndex === focusedIndex
                                    return (
                                        <SelectItem
                                            key={item.value}
                                            item={item}
                                            isSelected={isSelected}
                                            isFocused={isFocused}
                                            onSelect={handleSelect}
                                            onHover={() => !item.disabled && setFocusedIndex(enabledIndex)}
                                        />
                                    )
                                })}
                            </React.Fragment>
                        ))}
                    </div>,
                    document.body
                )}
        </div>
    )
}
